import React, { useEffect, useMemo, useState } from "react";
import { Button } from "@/components/ui/button";
import { Coins, Egg, Timer, Wheat, Sparkles } from "lucide-react";

/** Painted farm asset per level tier (0/undefined → sprout). */
const FARM_ART = {
  1: "/farm-assets/level1_sprout.png",
  2: "/farm-assets/level2_sapling.png",
  3: "/farm-assets/level3_farmhand.png",
  4: "/farm-assets/level4_farmer.png",
  5: "/farm-assets/level5_harvest_master.png",
  6: "/farm-assets/level6_legendary.png",
};
const TIER_LABEL = {
  1: "Sprout", 2: "Sapling", 3: "Farmhand",
  4: "Farmer", 5: "Harvest Master", 6: "Legendary Farmer",
};

const fmtCountdown = (s) => {
  if (s == null) return "";
  const h = Math.floor(s / 3600), m = Math.floor((s % 3600) / 60), ss = s % 60;
  return `${h}h ${String(m).padStart(2, "0")}m ${String(ss).padStart(2, "0")}s`;
};

/**
 * Painted "your farm" hero card for Shift Farm.
 * - Background is a level-specific Hay Day-style illustration.
 * - Content (name, level, XP, coins, eggs) is overlaid with a dark scrim.
 * - On first mount per session the scene zooms in from ~0.7×.
 * - Legendary (L6) adds a golden glow ring.
 */
export default function FarmScene({
  me, facilityLabel,
  canClaimEggs, nextEgg, onClaimEggs,
}) {
  const level = me?.level?.level || 1;
  const tierLabel = me?.level?.label || TIER_LABEL[level] || "Sprout";
  const art = FARM_ART[level] || FARM_ART[1];
  const isLegendary = level >= 6;

  // Play zoom-in only once per browser session.
  const [zoomed, setZoomed] = useState(false);
  useEffect(() => {
    const key = "shiftFarm.heroZoomed";
    if (sessionStorage.getItem(key)) { setZoomed(true); return; }
    sessionStorage.setItem(key, "1");
  }, []);

  const xpText = useMemo(() => {
    if (!me) return "…";
    if (me.level?.next_at != null) return `${me.xp} / ${me.level.next_at} XP`;
    return `MAX · ${me.xp} XP`;
  }, [me]);

  const progressPct = Math.round(100 * (me?.level?.progress || 0));

  return (
    <div
      className={`relative overflow-hidden rounded-2xl border shadow-sm ${
        isLegendary
          ? "border-amber-400 ring-2 ring-amber-300 shadow-amber-200/60 shadow-lg"
          : "border-slate-200"
      }`}
      data-testid="farm-hero"
    >
      {/* Painted background */}
      <div className="absolute inset-0 bg-[#0b1f2f]">
        <img
          src={art}
          alt={`${tierLabel} farm`}
          className={`w-full h-full object-cover ${zoomed ? "" : "farm-hero-zoom"}`}
          data-testid="farm-hero-art"
          draggable={false}
        />
      </div>
      {/* Sky-to-ground scrim so overlays stay legible */}
      <div className="absolute inset-0 bg-gradient-to-b from-black/5 via-transparent to-black/60 pointer-events-none" />
      {/* Idle sparkles for legendary */}
      {isLegendary && (
        <>
          <span className="farm-sparkle" style={{ left: "42%", top: "35%", animationDelay: "0.2s" }} />
          <span className="farm-sparkle" style={{ left: "58%", top: "45%", animationDelay: "0.9s" }} />
          <span className="farm-sparkle" style={{ left: "72%", top: "30%", animationDelay: "1.6s" }} />
        </>
      )}

      <div className="relative p-4 sm:p-5 min-h-[220px] sm:min-h-[260px] flex flex-col justify-between gap-3">
        {/* Top row — facility chip + coins / eggs */}
        <div className="flex items-start justify-between gap-2 flex-wrap">
          <div
            className="inline-flex items-center gap-1.5 rounded-full bg-white/85 backdrop-blur px-3 py-1 text-[11px] sm:text-xs font-semibold text-emerald-900 border border-white/60 shadow-sm"
            data-testid="farm-hero-facility"
          >
            <Wheat size={12} /> {facilityLabel || "Shift Farm"}
          </div>

          <div className="flex gap-2 sm:gap-2.5">
            <div
              className="rounded-xl bg-white/90 backdrop-blur border border-white/60 px-3 py-1.5 shadow-sm text-center min-w-[74px]"
              data-testid="farm-hero-coins"
            >
              <div className="flex items-center gap-1 justify-center text-amber-700">
                <Coins size={12} />
                <span className="text-[9px] uppercase tracking-widest font-bold">Coins</span>
              </div>
              <div className="font-display font-bold text-lg sm:text-xl tabular-nums text-amber-900 leading-tight">
                {me?.coins ?? 0}
              </div>
            </div>

            <div
              className="rounded-xl bg-white/90 backdrop-blur border border-white/60 px-3 py-1.5 shadow-sm text-center min-w-[110px] sm:min-w-[140px]"
              data-testid="farm-hero-eggs"
            >
              <div className="flex items-center gap-1 justify-center text-orange-700 mb-0.5">
                <Egg size={12} />
                <span className="text-[9px] uppercase tracking-widest font-bold">Coop</span>
              </div>
              {canClaimEggs ? (
                <Button
                  size="sm"
                  className="bg-orange-500 hover:bg-orange-600 w-full text-xs h-7"
                  onClick={onClaimEggs}
                  data-testid="farm-claim-eggs"
                >
                  <Egg size={12} className="mr-1" /> Collect
                </Button>
              ) : (
                <div
                  className="text-[11px] text-slate-700 inline-flex items-center gap-1 tabular-nums"
                  data-testid="farm-egg-countdown"
                >
                  <Timer size={12} /> {fmtCountdown(nextEgg)}
                </div>
              )}
            </div>
          </div>
        </div>

        {/* Bottom row — name + tier + XP bar */}
        <div className="text-white drop-shadow-[0_2px_6px_rgba(0,0,0,0.55)]">
          <div className="text-[11px] font-semibold uppercase tracking-widest opacity-90">
            Welcome back
          </div>
          <div
            className="font-display font-bold text-2xl sm:text-3xl leading-tight truncate"
            data-testid="farm-hero-name"
          >
            {me?.name || "Farmer"}
          </div>
          <div className="mt-1.5 flex items-center gap-2 flex-wrap">
            <span
              className={`inline-flex items-center gap-1 rounded-full px-2 py-0.5 text-[11px] font-bold border ${
                isLegendary
                  ? "bg-amber-300/95 border-amber-500 text-amber-950"
                  : "bg-white/95 border-white/60 text-emerald-900"
              }`}
              data-testid="farm-hero-tier"
            >
              {isLegendary && <Sparkles size={11} />}
              L{level} · {tierLabel}
            </span>
            <span className="text-[11px] font-semibold opacity-95 tabular-nums" data-testid="farm-hero-xp">
              {xpText}
            </span>
          </div>
          {me?.level?.progress != null && (
            <div className="mt-2 h-2 w-full max-w-[380px] rounded-full bg-black/40 overflow-hidden">
              <div
                className="h-full bg-gradient-to-r from-emerald-400 to-amber-300 rounded-full transition-all"
                style={{ width: `${progressPct}%` }}
                data-testid="farm-xp-progress"
              />
            </div>
          )}
        </div>
      </div>

      <style>{`
        @keyframes farmHeroZoom {
          0%   { transform: scale(0.72) translateY(24px); filter: blur(1.5px) saturate(0.7); opacity: 0 }
          60%  { transform: scale(1.02) translateY(0);    filter: blur(0)    saturate(1);    opacity: 1 }
          100% { transform: scale(1)    translateY(0);    filter: blur(0)    saturate(1);    opacity: 1 }
        }
        .farm-hero-zoom { animation: farmHeroZoom 1.6s cubic-bezier(.22,.9,.25,1) both; transform-origin: 52% 62%; }
        @media (prefers-reduced-motion: reduce) { .farm-hero-zoom { animation: none } }
        @keyframes farmSparkle {
          0%   { transform: translateY(10px) scale(0.5); opacity: 0 }
          30%  { opacity: 1 }
          100% { transform: translateY(-30px) scale(1.2); opacity: 0 }
        }
        .farm-sparkle {
          position: absolute; width: 14px; height: 14px; pointer-events: none;
          background: radial-gradient(circle, #fde68a 0%, rgba(253,230,138,0) 65%);
          animation: farmSparkle 2.4s ease-out infinite;
        }
      `}</style>
    </div>
  );
}
